import Seo from "../components/Seo";
import { Card, CardContent } from "../components/ui/card";
import { SEO_ROUTE_PROPS } from "../seo/metadata";

export default function PrivacyPolicy() {
	return (
		<div
			ref={(el) => el?.scrollIntoView()}
			className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14"
		>
			<Seo {...SEO_ROUTE_PROPS["/privacy"]} />
			<Card className="shadow-sm">
				<CardContent className="p-6 sm:p-8 lg:p-10">
					<div className="space-y-10 [&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_h1]:text-4xl [&_h1]:font-semibold [&_h1]:tracking-tight [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:tracking-tight [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:tracking-tight [&_li]:leading-7 [&_p]:leading-7 [&_section]:space-y-4 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6">
						<h1>Privacy Policy</h1>
						<p className="border-b pb-6 text-sm text-muted-foreground">
							Last updated: February 26, 2026
						</p>

						<section>
							<p>
								Clerical (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;)
								respects your privacy. This Privacy Policy explains how we
								collect, use, disclose, and safeguard personal information in
								connection with our website, marketing activities, and other
								interactions (collectively, the &quot;Service&quot;).
							</p>
						</section>

						<section>
							<h2>Scope of This Policy</h2>
							<p>
								Our services are intended for law firms and their authorized
								representatives. We generally treat personal information
								collected through the Service as relating to individuals acting
								in a professional capacity.
							</p>
							<p>
								When we process personal information on behalf of a law firm in
								our role as a service provider, that processing is governed by
								our service agreement with the firm. For transparency, we
								describe the types of data involved below, but if you are a
								client of a firm that uses Clerical and have questions about how
								your information is handled, please contact your
								attorney&rsquo;s office directly.
							</p>
						</section>

						<section>
							<h2>Personal Information We Collect</h2>

							<h3>Information You Provide</h3>
							<p>Personal information you may provide directly includes:</p>
							<ul>
								<li>
									Contact information, such as name, email address, phone
									number, and firm name, when you request a demo or contact us
								</li>
								<li>
									Billing and payment information when you subscribe to our
									services
								</li>
								<li>
									Communications you send to us through the website, email, or
									other channels
								</li>
							</ul>

							<h3>Information Processed on Behalf of Your Firm</h3>
							<p>
								When your firm uses our services, we process data on your
								firm&rsquo;s behalf as a service provider. This may include:
							</p>
							<ul>
								<li>Client names, contact information, and case details</li>
								<li>
									Immigration documents such as passports, birth certificates,
									employment letters, and government forms
								</li>
								<li>
									Voice call recordings and transcripts generated by our AI
									phone agent
								</li>
								<li>
									Messages and communications sent or received through our
									platform
								</li>
								<li>
									Case status, deadlines, and scheduling information from
									integrated systems such as Docketwise
								</li>
							</ul>
							<p>
								Your firm determines what client data is processed through our
								services. We process this data solely on your firm&rsquo;s
								behalf and according to your firm&rsquo;s instructions.
							</p>

							<h3>Information Collected Automatically</h3>
							<p>
								When you visit our website, we and our service providers may
								automatically collect:
							</p>
							<ul>
								<li>
									Device information, such as browser type, operating system,
									and language settings
								</li>
								<li>IP address and approximate location</li>
								<li>
									Usage information, including pages visited, time spent, and
									referring URLs
								</li>
							</ul>
						</section>

						<section>
							<h2>How We Use Personal Information</h2>
							<p>We use personal information for the following purposes:</p>
							<ul>
								<li>
									<strong>Service delivery:</strong> To provide, operate, and
									improve our automation services, including AI-powered phone
									answering, document processing, client follow-ups, and case
									monitoring
								</li>
								<li>
									<strong>Document processing:</strong> To extract and enter
									data from immigration documents on behalf of your firm
								</li>
								<li>
									<strong>Client communications:</strong> To generate and
									deliver messages to your firm&rsquo;s clients in their
									preferred language
								</li>
								<li>
									<strong>Account management:</strong> To communicate with you
									about your account, updates, and support requests
								</li>
								<li>
									<strong>Analytics and improvement:</strong> To analyze usage
									trends and improve our platform
								</li>
								<li>
									<strong>Compliance:</strong> To comply with legal obligations
									and enforce our terms
								</li>
							</ul>
						</section>

						<section>
							<h2>AI and Automated Processing</h2>
							<p>
								Our services use artificial intelligence and automated systems
								to process documents, handle phone calls, generate
								communications, and monitor cases. Data processed through these
								systems is used solely to provide the services your firm has
								engaged us to perform. We do not use your firm&rsquo;s client
								data to train general-purpose AI models.
							</p>
						</section>

						<section>
							<h2>How We Share Personal Information</h2>
							<p>
								We do not sell personal information. We may share personal
								information with:
							</p>
							<ul>
								<li>
									<strong>Service providers</strong> that help us operate our
									platform, such as cloud hosting, AI infrastructure, and
									payment processing
								</li>
								<li>
									<strong>Third-party integrations</strong> your firm has
									authorized, such as Docketwise or other case management
									systems
								</li>
								<li>
									<strong>Professional advisors</strong> such as legal,
									accounting, or insurance advisors as needed
								</li>
								<li>
									<strong>Authorities and regulators</strong> when required by
									law or to protect our rights, privacy, safety, or property
								</li>
								<li>
									<strong>Business transferees</strong> in connection with a
									merger, acquisition, financing, or sale of assets
								</li>
							</ul>
						</section>

						<section>
							<h2>Data Security</h2>
							<p>
								We implement administrative, technical, and physical safeguards
								designed to protect the information we process, including
								encryption of data in transit and at rest. However, no method of
								transmission over the Internet or electronic storage is
								completely secure, and we cannot guarantee absolute security.
							</p>
						</section>

						<section>
							<h2>Data Retention</h2>
							<p>
								We retain personal information for as long as necessary to
								fulfill the purposes described in this Privacy Policy, comply
								with legal obligations, and enforce our agreements. When your
								firm&rsquo;s engagement with us ends, we will delete or return
								your firm&rsquo;s client data in accordance with our service
								agreement, unless retention is required by applicable law.
							</p>
						</section>

						<section>
							<h2>Cookies</h2>
							<p>
								We use cookies and similar technologies to enhance your
								experience and analyze website usage. Most browsers allow you to
								manage cookies through their settings. Disabling cookies may
								affect certain features of our website.
							</p>
						</section>

						<section>
							<h2>Your Privacy Rights</h2>
							<p>
								Depending on where you reside, you may have rights regarding
								your personal information under applicable privacy laws,
								including:
							</p>
							<ul>
								<li>
									<strong>Right to know</strong> whether we process your
									personal information and to access the information we hold
								</li>
								<li>
									<strong>Right to delete</strong> your personal information,
									subject to certain exceptions
								</li>
								<li>
									<strong>Right to correct</strong> inaccurate personal
									information
								</li>
								<li>
									<strong>Right to opt out</strong> of the sale or sharing of
									personal information for targeted advertising (we do not
									currently sell or share personal information for these
									purposes)
								</li>
							</ul>
							<p>
								To exercise any of these rights, please contact us at{" "}
								<a href="mailto:support@clerical.com">support@clerical.com</a>.
								We will verify your identity before processing your request and
								respond within the timeframe required by applicable law.
							</p>
							<p>
								If you are a client of a law firm that uses Clerical, please
								direct any data requests to your attorney&rsquo;s office, as
								they control how your information is processed through our
								platform.
							</p>
						</section>

						<section>
							<h2>Children&rsquo;s Privacy</h2>
							<p>
								Our services are not directed at individuals under 18. We do not
								knowingly collect personal information directly from children.
								Immigration case files processed on behalf of your firm may
								include information about minor dependents as part of the case
								record; this information is handled in accordance with the same
								protections applied to all client data.
							</p>
						</section>

						<section>
							<h2>Changes to This Policy</h2>
							<p>
								We may update this Privacy Policy from time to time. If we make
								material changes, we will update the effective date and provide
								notice as required by law. Continued use of the Service after
								changes take effect indicates acceptance of the updated Privacy
								Policy.
							</p>
						</section>

						<section>
							<h2>Contact Us</h2>
							<p>
								If you have questions about this Privacy Policy or wish to
								exercise your privacy rights, please contact us at{" "}
								<a href="mailto:support@clerical.com">support@clerical.com</a>{" "}
								or by mail at:
							</p>
							<p>
								Clerical
								<br />
								2261 Market Street STE 22734
								<br />
								San Francisco, CA 94114
							</p>
						</section>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
